import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { CheckCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const CATEGORIES = [
  { value: 'account_or_listing',      key: 'contact.category.account' },
  { value: 'partnership_or_business', key: 'contact.category.partnership' },
  { value: 'other',                   key: 'contact.category.other' },
];

/**
 * Public contact form for the Help & Contact page.
 *
 * Same graceful-degradation pattern as ForgotPasswordPage: wraps the
 * inner form in the GoogleReCaptchaProvider only when a site key is
 * configured; without the key the form still posts (backend accepts
 * because RECAPTCHA_SECRET_KEY is also unset in that scenario).
 */
export const ContactForm = () => {
  if (RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        scriptProps={{ async: true, defer: true, appendTo: 'head' }}
      >
        <ContactFormInner />
      </GoogleReCaptchaProvider>
    );
  }
  return <ContactFormInner />;
};

const ContactFormInner = () => {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('account_or_listing');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const validate = () => {
    if (name.trim().length < 2) return t('contact.errors.name');
    // RFC-ish light check; the real validation happens server-side.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return t('contact.errors.email');
    if (!category) return t('contact.errors.category');
    if (message.trim().length < 10) return t('contact.errors.message');
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);
    let recaptcha_token = null;
    if (executeRecaptcha) {
      try {
        recaptcha_token = await executeRecaptcha('contact_form');
      } catch (rcErr) {
        console.warn('reCAPTCHA execute failed:', rcErr);
      }
    }
    try {
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        category,
        message: message.trim(),
        recaptcha_token,
        lang: i18n.language || 'fr',
      });
      setSubmitted(true);
    } catch (postErr) {
      const status = postErr?.response?.status;
      if (status === 429) {
        setError(t('contact.errors.rateLimit'));
      } else {
        setError(t('contact.errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="contact-confirmation"
        className="bg-success/10 border border-success rounded-lg p-6 text-center"
      >
        <CheckCircle size={48} weight="fill" className="mx-auto text-success mb-3" />
        <h3 className="text-xl font-semibold mb-2">{t('contact.confirmation.title')}</h3>
        <p className="text-text_muted">{t('contact.confirmation.body')}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5 mt-6"
      data-testid="contact-form"
      noValidate
    >
      <div>
        <Label htmlFor="contact-name">{t('contact.fields.name')} *</Label>
        <Input
          id="contact-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          required
          data-testid="contact-name-input"
        />
      </div>

      <div>
        <Label htmlFor="contact-email">{t('contact.fields.email')} *</Label>
        <Input
          id="contact-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="contact-email-input"
        />
      </div>

      <div>
        <Label htmlFor="contact-category">{t('contact.fields.category')} *</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger id="contact-category" data-testid="contact-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value} data-testid={`contact-category-${c.value}`}>
                {t(c.key)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="contact-message">{t('contact.fields.message')} *</Label>
        <Textarea
          id="contact-message"
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={5000}
          required
          data-testid="contact-message-textarea"
        />
        <p className="text-xs text-text_muted mt-1">
          {message.length}/5000
        </p>
      </div>

      {error && (
        <p className="text-danger text-sm" data-testid="contact-error">{error}</p>
      )}

      <Button
        type="submit"
        disabled={loading}
        className="bg-primary text-white"
        data-testid="contact-submit-button"
      >
        {loading ? t('contact.submitting') : t('contact.submit')}
      </Button>

      <p className="text-xs text-text_muted">
        {t('contact.recaptchaNotice')}
      </p>
    </form>
  );
};
